import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';
import { formatDateIso } from '@/utilities/dateutils';

export class BibAssign extends BaseObj {
    public ID: number;
    public __id: number;

    public AssignSort: number | null;
    public __assignsort: number | null;

    public Bib: number;
    public __bib: number;

    public Blocked: boolean;
    public __blocked: boolean;

    public CategoryID: number | null;
    public __categoryid: number | null;

    public ContestantID: number | null;
    public __contestantid: number | null;

    public EventID: number;
    public __eventid: number;

    public GroupID: number | null;
    public __groupid: number | null;

    public StartTime: Date | null;
    public __starttime: Date | null;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AssignSort?: number | null;
        Bib?: number;
        Blocked?: boolean;
        CategoryID?: number | null;
        ContestantID?: number | null;
        EntityState?: EntityState;
        EventID?: number;
        GroupID?: number | null;
        StartTime?: Date | null;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<BibAssign>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('BibAssign tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AssignSort = values.AssignSort ?? null;
        this.__assignsort = values.AssignSort ?? null;
        Object.defineProperty(this, 'AssignSort', {
            get(): number | null {
                return this.__assignsort;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('AssignSort', this.__assignsort, value);
                this.__assignsort = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Bib = values.Bib ?? 0;
        this.__bib = values.Bib ?? 0;
        Object.defineProperty(this, 'Bib', {
            get(): number {
                return this.__bib;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Bib', this.__bib, value);
                this.__bib = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Blocked = values.Blocked ?? false;
        this.__blocked = values.Blocked ?? false;
        Object.defineProperty(this, 'Blocked', {
            get(): boolean {
                return this.__blocked;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Blocked', this.__blocked, value);
                this.__blocked = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.CategoryID = values.CategoryID ?? null;
        this.__categoryid = values.CategoryID ?? null;
        Object.defineProperty(this, 'CategoryID', {
            get(): number | null {
                return this.__categoryid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('CategoryID', this.__categoryid, value);
                this.__categoryid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.ContestantID = values.ContestantID ?? null;
        this.__contestantid = values.ContestantID ?? null;
        Object.defineProperty(this, 'ContestantID', {
            get(): number | null {
                return this.__contestantid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('ContestantID', this.__contestantid, value);
                this.__contestantid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GroupID = values.GroupID ?? null;
        this.__groupid = values.GroupID ?? null;
        Object.defineProperty(this, 'GroupID', {
            get(): number | null {
                return this.__groupid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('GroupID', this.__groupid, value);
                this.__groupid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartTime = values.StartTime ?? null;
        this.__starttime = values.StartTime ?? null;
        Object.defineProperty(this, 'StartTime', {
            get(): Date | null {
                return this.__starttime;
            },
            set(value: Date | null) {
                this.__entityStateHandler.propertyChanged('StartTime', this.__starttime, value);
                this.__starttime = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AssignSort": ${JSON.stringify(this.AssignSort)}` +
            ',' +
            `"Bib": ${JSON.stringify(this.Bib)}` +
            ',' +
            `"Blocked": ${JSON.stringify(this.Blocked)}` +
            ',' +
            `"CategoryID": ${JSON.stringify(this.CategoryID)}` +
            ',' +
            `"ContestantID": ${JSON.stringify(this.ContestantID)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"GroupID": ${JSON.stringify(this.GroupID)}` +
            ',' +
            `"StartTime": ${this.StartTime ? '"' + formatDateIso(this.StartTime) + '"' : null}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of BibAssign from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AssignSort?: number | null;
        Bib?: number;
        Blocked?: boolean;
        CategoryID?: number | null;
        ContestantID?: number | null;
        EventID?: number;
        GroupID?: number | null;
        StartTime?: Date | null;
        EntityState?: 'Added' | 'Unchanged';
    }): BibAssign {
        return new BibAssign(values);
    }

    public static _FromServerJson(json: any): BibAssign {
        return new BibAssign({
            ID: json.ID,
            AssignSort: json.AssignSort ?? null,
            Bib: json.Bib,
            Blocked: json.Blocked,
            CategoryID: json.CategoryID ?? null,
            ContestantID: json.ContestantID ?? null,
            EventID: json.EventID,
            GroupID: json.GroupID ?? null,
            StartTime: json.StartTime ? (json.StartTime instanceof Date ? json.StartTime : new Date(json.StartTime)) : null,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of BibAssign from another instance of BibAssign.
     * @param other The BibAssign you want to clone
     * @returns A cloned BibAssign
     */
    public static FromOther(other: BibAssign): BibAssign {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return BibAssign._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return BibAssign.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: ('ID' | 'AssignSort' | 'Bib' | 'Blocked' | 'CategoryID' | 'ContestantID' | 'EventID' | 'GroupID' | 'StartTime')[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'BibAssign';
}
